import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Logo } from "./Logo";
import { LogOut, History, Settings, Users, LayoutDashboard, ChevronDown, ShieldCheck, Crown, Activity } from "lucide-react";
import { useState } from "react";

const ROLE_LABEL = {
  super_admin: "Super Admin",
  admin: "Admin",
  moderator: "Moderator",
  staff: "Staff",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const role = user?.role;
  const isAdmin = role === "super_admin" || role === "admin";
  const isModeratorOrAbove = isAdmin || role === "moderator";

  const links = [
    { to: "/quote-portal/products", label: "Products", icon: LayoutDashboard, show: true },
    { to: "/quote-portal/quotes",   label: "Quotes",   icon: History,         show: true },
    { to: "/quote-portal/admin/pricing", label: "Pricing", icon: Settings,    show: isAdmin },
    { to: "/quote-portal/admin/pricing-health", label: "Health", icon: Activity, show: isAdmin },
    { to: "/quote-portal/admin/users",   label: "Users",   icon: Users,       show: isModeratorOrAbove },
  ].filter((l) => l.show);

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col" data-testid="layout-root">
      {/* TOP HEADER */}
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-slate-200">
        <div className="px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-6 max-w-[1600px] mx-auto">
          <Link to="/quote-portal" className="flex items-center gap-3">
            <Logo height={42} />
            <div className="hidden md:block pl-4 border-l border-gray-200">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#fda500] font-bold">Quoting Suite</div>
              <div className="text-sm font-semibold text-[#0A1128]">Rubber Ramp Calculator</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1" data-testid="top-nav">
            {links.map((l) => {
              const Icon = l.icon;
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3.5 py-2 text-sm rounded-md transition-colors ${
                      isActive
                        ? "bg-[#254391] text-white font-medium"
                        : "text-gray-700 hover:bg-gray-100 hover:text-[#254391]"
                    }`
                  }
                >
                  <Icon size={15} strokeWidth={1.7} />
                  <span>{l.label}</span>
                </NavLink>
              );
            })}
          </nav>

          {/* User */}
          <div className="relative">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="flex items-center gap-2 pl-2 pr-3 py-1.5 hover:bg-gray-100 rounded-md transition"
              data-testid="user-menu-btn"
            >
              <div className="w-8 h-8 bg-[#254391] text-white text-xs font-bold flex items-center justify-center rounded-md">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div className="text-left hidden sm:block">
                <div className="text-sm font-semibold leading-tight" data-testid="user-name">{user?.name}</div>
                <div className={`text-[10px] uppercase tracking-[0.16em] font-bold flex items-center gap-1 px-2 py-0.5 rounded-full ${
                  role === "super_admin" ? "bg-[#0A1128] text-[#fda500]" :
                  role === "admin" ? "bg-[#254391] text-white" :
                  role === "moderator" ? "bg-slate-600 text-white" :
                  "bg-slate-100 text-slate-700"
                }`}>
                  {role === "super_admin" && <Crown size={9} />}
                  {role === "admin" && <ShieldCheck size={9} />}
                  {ROLE_LABEL[role] || role}
                </div>
              </div>
              <ChevronDown size={14} className="text-gray-500" />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 shadow-lg z-50 rounded-md overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-200">
                    <div className="text-sm font-semibold">{user?.name}</div>
                    <div className="text-xs text-gray-500 truncate">{user?.email}</div>
                  </div>
                  <button
                    onClick={() => { setMenuOpen(false); nav("/quote-portal/logout"); }}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-red-50 text-red-700 flex items-center gap-2"
                    data-testid="logout-btn"
                  >
                    <LogOut size={14} /> Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        {/* Orange accent line */}
        <div className="h-[3px] bg-gradient-to-r from-[#fda500] via-[#fda500] to-transparent" />
      </header>

      {/* PAGE CONTENT */}
      <main key={loc.pathname} className="flex-1 eas-slide-up">
        <div className="px-6 py-6 md:px-8 md:py-8 max-w-[1600px] mx-auto w-full">
          <Outlet />
        </div>
      </main>

      <footer className="border-t border-gray-200 bg-white py-3 px-6 text-center text-xs text-gray-500">
        Ezy Access Solutions · Built for Access. Made with Care. · ezyaccess.com.au
      </footer>
    </div>
  );
}
